// StockReport.jsx / StockCheckByBranch.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import './report.css';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/apiService';

// ----------------- HELPERS -----------------
const numberFmt = (n) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(+n) ? +n : 0);

// ----------------- COMPONENT -----------------
const StockCheckByBranch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]); // wide rows: 1 row per item
  const [branchColumns, setBranchColumns] = useState([]); // dynamic branch names (columns)
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Branch filter
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // Item dropdown (Measure)
  const [itemOptions, setItemOptions] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null); // { value, label }

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 50,
    totalRecords: 0,
    totalPages: 1,
  });

  // ----------------- Fetch Branches -----------------
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');

      const list = res?.data?.result || [];
      const mapped = list.map((b) => ({
        value: b.id,
        label: b.branchName,
      }));

      setBranches(mapped);
    } catch (e) {
      console.error('Failed to fetch branches', e);
    }
  };

  // ----------------- Fetch Items for dropdown -----------------
  const fetchItems = async () => {
    try {
      setItemsLoading(true);
      const apiService = new ApiService();

      // Reuse the same API you are using in Menu: GET_ITEM_QUICK
      // Here we load a larger page once for dropdown options
      const pageNo = 1;
      const pageSize = 5000; // adjust if needed
      const parameters = `?Name=&pageNo=${pageNo}&pageSize=${pageSize}`;

      const res = await apiService.getApiParamater('GET_ITEM_QUICK', parameters);

      if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
        const items = res.data.result || [];

        const options = items
          .map((item) => ({
            // adjust these properties based on your API response
            value: item.ItemName || item.ItemDescription || item.Name || item.itemName || item.name,
            label: item.ItemName || item.ItemDescription || item.Name || item.itemName || item.name,
          }))
          .filter((o) => o.value && o.label);

        setItemOptions(options);
      } else {
        setItemOptions([]);
      }
    } catch (error) {
      console.error('Failed to fetch items for dropdown', error);
      setItemOptions([]);
    } finally {
      setItemsLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchItems();
  }, []);

  // ----------------- Fetch Stock Report -----------------
  const fetchData = async () => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      // @Res_Branch_Id = branchId || 0 for all
      params.append('Res_Branch_Id', selectedBranch?.value || 0);
      params.append('ReportType', 'Stock Report');

      // Also send BranchId explicitly
      params.append('BranchId', selectedBranch?.value || 0);

      // Use selectedItem.label as Measure
      const measureValue = selectedItem?.label || '';

      // @Measure = 'item name' (NULL if not selected)
      if (measureValue && measureValue.trim() !== '') {
        params.append('Measure', measureValue.trim());
      } else {
        params.append('Measure', 'NULL'); // or blank, depending on API expectation
      }

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];

      if (!result.length) {
        setRows([]);
        setBranchColumns([]);
        setPagination((prev) => ({
          ...prev,
          currentPage: 1,
          totalRecords: 0,
          totalPages: 1,
        }));
        return;
      }

      // Determine which keys are branch columns from the first record
      const NON_BRANCH_KEYS = ['ItemDescription', 'Item Description', 'ItemName', 'Item Name', 'Total Qty', 'TotalQty'];

      const first = result[0];
      let dynamicBranches = Object.keys(first).filter((k) => !NON_BRANCH_KEYS.includes(k));

      // Optional: sort branchColumns alphabetically
      dynamicBranches = dynamicBranches.sort((a, b) => String(a).localeCompare(String(b)));

      // Build wide rows:
      //  { itemDescription, [branch1]: qty1, [branch2]: qty2, ..., totalQty }
      const wideRows = result.map((item) => {
        const itemDescription = item.ItemDescription || item['Item Description'] || item.ItemName || item['Item Name'] || '';

        const row = {
          itemDescription,
          totalQty: Number(item['Total Qty'] ?? item.TotalQty ?? 0),
        };

        dynamicBranches.forEach((br) => {
          row[br] = Number(item[br]) || 0;
        });

        return row;
      });

      // Sort by item name initially
      wideRows.sort((a, b) => String(a.itemDescription || '').localeCompare(String(b.itemDescription || '')));

      setRows(wideRows);
      setBranchColumns(dynamicBranches);

      const pageSize = pagination.pageSize;
      const totalRecords = wideRows.length;
      const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
        pageSize,
        totalRecords,
        totalPages,
      }));
    } catch (e) {
      console.error(e);
      setErr('Failed to fetch stock report. Please try again.');
      setRows([]);
      setBranchColumns([]);
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
        totalRecords: 0,
        totalPages: 1,
      }));
    } finally {
      setLoading(false);
    }
  };

  const onSearch = () => fetchData();

  // ----------------- Sorting -----------------
  const [sortState, setSortState] = useState({
    key: 'itemDescription',
    dir: 'asc',
  });

  const onSort = (key) => setSortState((prev) => (prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));

  const sortedRows = useMemo(() => {
    const { key, dir } = sortState;
    const copy = [...rows];

    copy.sort((a, b) => {
      const av = a[key];
      const bv = b[key];

      // itemDescription is string, everything else treated as numeric
      if (key === 'itemDescription') {
        return dir === 'asc' ? String(av ?? '').localeCompare(String(bv ?? '')) : String(bv ?? '').localeCompare(String(av ?? ''));
      }

      const an = Number(av) || 0;
      const bn = Number(bv) || 0;
      return dir === 'asc' ? an - bn : bn - an;
    });

    return copy;
  }, [rows, sortState]);

  // ----------------- Client-side page slice -----------------
  const pagedRows = useMemo(() => {
    const { currentPage, pageSize } = pagination;
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedRows.slice(start, end);
  }, [sortedRows, pagination]);

  // ----------------- Totals (per branch + grand total) -----------------
  const totals = useMemo(() => {
    const perBranch = {};
    branchColumns.forEach((br) => {
      perBranch[br] = sortedRows.reduce((s, r) => s + (Number(r[br]) || 0), 0);
    });

    const totalQty = sortedRows.reduce((s, r) => s + (Number(r.totalQty) || 0), 0);

    return { perBranch, totalQty };
  }, [sortedRows, branchColumns]);

  // ----------------- CSV Export -----------------
  const exportCSV = () => {
    if (!sortedRows.length) return;

    const headers = ['Item Name', ...branchColumns, 'Total Qty'].map((h) => `"${h.replace(/"/g, '""')}"`).join(',');

    const body = sortedRows
      .map((r) => {
        const rowVals = [r.itemDescription, ...branchColumns.map((br) => r[br]), r.totalQty];

        return rowVals.map((val) => `"${String(val ?? '').replace(/"/g, '""')}"`).join(',');
      })
      .join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  // ----------------- Pagination controls -----------------
  const goToPage = (page) => {
    setPagination((p) => {
      if (page < 1 || page > p.totalPages) return p;
      return { ...p, currentPage: page };
    });
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value) || 50;
    setPagination((p) => {
      const totalPages = Math.max(1, Math.ceil(p.totalRecords / newSize));
      return {
        ...p,
        pageSize: newSize,
        currentPage: 1,
        totalPages,
      };
    });
  };

  const renderPageButtons = () => {
    const { currentPage, totalPages } = pagination;
    const buttons = [];

    if (totalPages <= 7) {
      for (let p = 1; p <= totalPages; p++) {
        buttons.push(
          <li key={p} className={`page-item ${p === currentPage ? 'active' : ''}`}>
            <button className="page-link" onClick={() => goToPage(p)}>
              {p}
            </button>
          </li>
        );
      }
      return buttons;
    }

    const add = (p) =>
      buttons.push(
        <li key={p} className={`page-item ${p === currentPage ? 'active' : ''}`}>
          <button className="page-link" onClick={() => goToPage(p)}>
            {p}
          </button>
        </li>
      );

    add(1);
    if (currentPage > 3) {
      buttons.push(
        <li key="start-ellipsis" className="page-item disabled">
          <span className="page-link">…</span>
        </li>
      );
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let p = start; p <= end; p++) add(p);

    if (currentPage < totalPages - 2) {
      buttons.push(
        <li key="end-ellipsis" className="page-item disabled">
          <span className="page-link">…</span>
        </li>
      );
    }

    add(totalPages);

    return buttons;
  };

  const getPageInfoText = () => {
    const { currentPage, pageSize, totalRecords } = pagination;
    if (!totalRecords) return 'No records';
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(totalRecords, currentPage * pageSize);
    return `Showing ${start}–${end} of ${totalRecords}`;
  };

  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Filters */}
      <div className="row g-3 align-items-end mb-3">
        {/* Branch */}
        <div className="col-md-4">
          <label className="form-label">Select Branch</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedBranch}
            onChange={(opt) => setSelectedBranch(opt)}
            options={branches}
            placeholder="All Branches"
            isClearable
          />
        </div>

        {/* Item (Measure) dropdown */}
        <div className="col-md-4">
          <label className="form-label">Item</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedItem}
            onChange={(opt) => setSelectedItem(opt)}
            options={itemOptions}
            placeholder={itemsLoading ? 'Loading items...' : 'Select an item (optional)'}
            isClearable
            isLoading={itemsLoading}
          />
        </div>

        <div className="col-md-4 d-flex gap-3 align-items-end">
          <button className="addItem-btn" onClick={onSearch} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>

          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!sortedRows.length}>
            Export CSV
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="POsgridshad rounded p-0 mt-2">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead>
              <tr>
                <th className="stock-item-col" onClick={() => onSort('itemDescription')} style={{ cursor: 'pointer' }}>
                  Item Name
                  {sortState.key === 'itemDescription' ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>

                {branchColumns.map((br) => (
                  <th
                    key={br}
                    className="text-end"
                    onClick={() => onSort(br)}
                    style={{
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {br}
                    {sortState.key === br ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}

                <th
                  className="text-end"
                  onClick={() => onSort('totalQty')}
                  style={{
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Total Qty
                  {sortState.key === 'totalQty' ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              </tr>
            </thead>

            <tbody>
              {!loading && pagedRows.length === 0 && (
                <tr>
                  <td colSpan={2 + branchColumns.length} className="text-center p-4">
                    No data found
                  </td>
                </tr>
              )}

              {!loading &&
                pagedRows.map((r, index) => (
                  <tr key={`${r.itemDescription}-${index}`}>
                    <td className="stock-item-col">{r.itemDescription}</td>
                    {branchColumns.map((br) => (
                      <td key={br} className="text-end">
                        {numberFmt(r[br])}
                      </td>
                    ))}
                    <td className="text-end">{numberFmt(r.totalQty)}</td>
                  </tr>
                ))}

              {loading && (
                <tr>
                  <td colSpan={2 + branchColumns.length} className="text-center p-4">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>

            {sortedRows.length > 0 && (
              <tfoot>
                <tr className="fw-bold bg-light">
                  <td>Total</td>
                  {branchColumns.map((br) => (
                    <td key={br} className="text-end">
                      {numberFmt(totals.perBranch[br] || 0)}
                    </td>
                  ))}
                  <td className="text-end">{numberFmt(totals.totalQty)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination bar */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center px-3 py-3 gap-2 border-top">
          <div>
            <nav aria-label="Stock pagination">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.currentPage === 1 || loading ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goToPage(pagination.currentPage - 1)} disabled={pagination.currentPage === 1 || loading}>
                    ‹ Prev
                  </button>
                </li>

                {renderPageButtons()}

                <li className={`page-item ${pagination.currentPage === pagination.totalPages || loading ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => goToPage(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages || loading}
                  >
                    Next ›
                  </button>
                </li>
              </ul>
            </nav>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <small className="text-muted">{getPageInfoText()}</small>

            <div className="d-flex align-items-center gap-1">
              <small className="text-muted">Rows per page:</small>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                disabled={loading}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(StockCheckByBranch);
